import { dateUtils, type Optional } from '@festina-life/core'
import {
  type CalendarDate,
  type DateValue,
  fromDate,
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  startOfYear,
  toCalendarDate,
  toZoned,
} from '@internationalized/date'
import type { PointerEventHandler } from 'react'

export type DateFieldGranularity = 'minute' | 'day' | 'month' | 'year'

export const clampCalendarDate = ({
  date,
  min,
  max,
}: {
  date: DateValue
  min: DateValue | null | undefined
  max: DateValue | null | undefined
}): CalendarDate => {
  if (min && date.compare(min) < 0) {
    return toCalendarDate(min)
  }
  if (max && date.compare(max) > 0) {
    return toCalendarDate(max)
  }
  return toCalendarDate(date)
}

/**
 * This is needed since otherwise the button inside the picker would always get focus unless segments are directly clicked, which is a bit weird.
 */
export const handleDatePickerPointerDown: PointerEventHandler<HTMLElement> = (
  event,
) => {
  const target = event.target as HTMLElement
  const group = event.currentTarget as HTMLElement
  if (!group.contains(target)) {
    return
  }
  const dateSegmentSelector = '[role="spinbutton"], [data-type]'

  const hasClickedDateSegment = target.closest(dateSegmentSelector)
  const hasClickedDateButton = target.closest(
    'button, [aria-haspopup="dialog"]',
  )

  if (hasClickedDateSegment || hasClickedDateButton) {
    return
  }

  const { activeElement } = document
  const hasSegmentFocus =
    !!activeElement &&
    group.contains(activeElement) &&
    !!activeElement.closest(dateSegmentSelector)
  if (hasSegmentFocus) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  const firstDateSegment = group.querySelector<HTMLElement>(dateSegmentSelector)
  if (firstDateSegment) {
    event.preventDefault()
    // Stops React Aria's group press handler from moving focus to the last tabbable element (the button).
    event.stopPropagation()
    // Focus synchronously so typing right after the press reaches the segment.
    firstDateSegment.focus()
    // Fallback in case the browser's own mouse focus handling moves focus out of the field.
    requestAnimationFrame(() => {
      if (!group.contains(document.activeElement)) {
        firstDateSegment.focus()
      }
    })
  }
}

/** True when some, but not all, date segments inside the element are filled. */
export const hasPartialDateSegments = (element: Element) =>
  !!element.querySelector('[role="spinbutton"]:not([aria-valuenow])') &&
  !!element.querySelector('[role="spinbutton"][aria-valuenow]')

/** A year below 1000 may still be being typed, so its changes wait for blur. */
export const hasShortYear = (value: Optional<DateValue> | null) =>
  !!value && value.year < 1000

export const normalizeDateValueForSegments = <T extends Optional<DateValue>>(
  value: T,
  granularity: DateFieldGranularity,
) => {
  if (!value) {
    return value
  }
  switch (granularity) {
    case 'year':
      return startOfYear(value) as T
    case 'month':
      return startOfMonth(value) as T
    case 'day':
    case 'minute':
      return value
  }
}

// Date-times are UTC on the server but edited in the user's local time zone.
// Zoned values keep their offset, so repeated local hours round-trip correctly.
const parseExternal = (value: string, granularity: DateFieldGranularity) =>
  granularity === 'minute'
    ? fromDate(dateUtils.dateUtcAsLocal(value).toDate(), getLocalTimeZone())
    : parseDate(value)

export const mapFromExternal = (
  value: string | undefined,
  granularity: DateFieldGranularity = 'day',
): DateValue | undefined =>
  normalizeDateValueForSegments(
    value ? parseExternal(value, granularity) : undefined,
    granularity,
  )

export const mapToExternal = ({
  value,
  granularity = 'day',
}: {
  value: DateValue | null
  granularity?: DateFieldGranularity
}) => {
  const normalizedValue = normalizeDateValueForSegments(value, granularity)
  if (!normalizedValue) {
    return ''
  }

  return granularity === 'minute'
    ? toZoned(normalizedValue, getLocalTimeZone())
        .toAbsoluteString()
        .slice(0, dateUtils.SERVER_DATE_TIME_FORMAT.length)
    : toCalendarDate(normalizedValue).toString()
}

export const addDays = (date: string | undefined, days: number) =>
  date && days ? parseDate(date).add({ days }).toString() : date

export const isOutOfBounds = ({
  value,
  granularity = 'day',
  min,
  max,
}: {
  value: DateValue | null
  granularity?: DateFieldGranularity
  min?: string
  max?: string
}) => {
  const normalizedValue = normalizeDateValueForSegments(value, granularity)
  const minValue = mapFromExternal(min, granularity)
  const maxValue = mapFromExternal(max, granularity)
  return (
    !!normalizedValue &&
    ((!!minValue && normalizedValue.compare(minValue) < 0) ||
      (!!maxValue && normalizedValue.compare(maxValue) > 0))
  )
}
